/**
 * MenuItem — dropdown menu item button.
 *
 * Extracts the repeated pattern from Shell.tsx dropdown (3 identical buttons).
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { clsx } from 'clsx'
import type { LucideIcon } from 'lucide-react'

export interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Lucide icon component. */
  icon: LucideIcon
  /** Menu item label. */
  label: string
  /** Danger variant (red text, e.g., "Sign Out"). */
  variant?: 'default' | 'danger'
}

/** Dropdown menu item with icon and label. */
export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  ({ icon: Icon, label, variant = 'default', className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={clsx(
          'w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors cursor-pointer bg-transparent border-none',
          variant === 'default' && 'text-content-secondary hover:bg-surface-secondary',
          variant === 'danger' && 'text-error hover:bg-error/8',
          className,
        )}
        {...props}
      >
        <Icon className={clsx('w-4 h-4', variant === 'default' && 'text-content-tertiary')} />
        {label}
      </button>
    )
  },
)

MenuItem.displayName = 'MenuItem'
